import { PlusOutlined } from '@ant-design/icons'
import { Image, Page, Text, View } from '@react-pdf/renderer'
import {
  AutoComplete,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Radio,
  Row,
  Select,
  Typography,
} from 'antd'
import { Dayjs } from 'dayjs'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { PageSubheader } from 'features/reporting/pdfGeneration/components/shared/PageSubHeader'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { formatDate } from 'features/reporting/pdfGeneration/utils/formatters'
import { PdfProps, ReportSection } from 'features/reporting/types'
import { CalibrationFileUpload } from 'pages/reporting/reports/raut/components/CalibrationFileUpload'
import { useCalibrationNdeUpload } from 'pages/reporting/reports/raut/hooks/useCalibrationNdeUpload'
import {
  CalibrationEntryMode,
  CalibrationPageData,
  calibrationPageDataSchema,
  MainInfo,
} from 'pages/reporting/reports/raut/rautReportData'
import { useRautReportOps } from 'pages/reporting/reports/raut/RautReportProvider'
import { normalizeCalibrationFormData } from 'pages/reporting/reports/raut/utils/normalizeCalibrationFormData'
import {
  TYPE_TO_METHOD_TITLE,
  TYPE_TO_PROCEDURE,
} from 'pages/reporting/shared/constants'
import { useUnitSystem } from 'pages/reporting/shared/contexts/UnitSystemContext'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import { ImageUpload } from 'pages/reporting/shared/utils/ImageUpload'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoInspection,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
  RolodexEntryType,
} from 'types'

const requiredCalibrationFields = [
  'procedure',
  'scanMethods',
  'testingUnit',
  'model',
  'instrumentSN',
  'scannerType',
  'scannerSN',
  ['xMotor', 'function'],
  ['yMotor', 'function'],
  ['calibrationDataFile', 'originalName'],
  ['calibrationImage', 'imagePath'],
  // Technique fields are now checked per-technique in the completion logic
]

const DEFAULT_CALIBRATION_ENTRY_MODE: CalibrationEntryMode = 'auto'

const { Option } = Select
const AntText = Typography.Text

/**
 * Styled wrapper for the entire calibration form page
 * Provides consistent padding and max-width constraints
 */
const CalibrationPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

/**
 * FormSectionCard - Card container for form sections with subtle elevation
 * Matches Cover.tsx pattern for consistent styling
 */
const FormSectionCard = styled.div`
  background: ${({ theme }) =>
    theme.forecast.cardBackground || theme.forecast.background1};
  border: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
  border-radius: 0.5rem;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 0.0625rem 0.25rem
    ${({ theme }) => theme.forecast.boxShadow || 'rgba(0, 0, 0, 0.06)'};
`

/**
 * FormSectionHeader - Section heading within cards
 */
const FormSectionHeader = styled.h3`
  margin: 0 0 1rem 0;
  padding: 0 0 0.75rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  letter-spacing: 0.025em;
  text-transform: uppercase;
  border-bottom: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
`

const RequiredAsterisk = styled.span`
  color: ${({ theme }) => theme.forecast.danger};
  margin-right: 0.25rem;
`

const AdvancedOptionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
`

const AdvancedOptionsPanel = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px dashed
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
`

/**
 * FormFieldGroup - Grid layout for form fields with responsive columns
 */
const FormFieldGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
  gap: 1rem;

  .ant-form-item {
    margin-bottom: 0;
  }

  & + & {
    margin-top: 1rem;
  }
`

/**
 * Styled technique card with improved visual hierarchy
 */
const TechniqueCard = styled(Card)`
  && {
    margin-bottom: 1rem;
    border-radius: 0.5rem;
    border: 1px solid
      ${({ theme }) =>
        theme.forecast.cardDividerBackground || theme.forecast.border};
    box-shadow: 0 0.0625rem 0.1875rem
      ${({ theme }) => theme.forecast.boxShadow || 'rgba(0, 0, 0, 0.03)'};
    background: ${({ theme }) =>
      theme.forecast.cardBackground || theme.forecast.background1};
    transition: box-shadow 0.2s ease;

    &:hover {
      box-shadow: 0 0.125rem 0.5rem
        ${({ theme }) => theme.forecast.boxShadow || 'rgba(0, 0, 0, 0.06)'};
    }

    &:last-of-type {
      margin-bottom: 0;
    }

    .ant-card-head {
      border-bottom: 1px solid
        ${({ theme }) =>
          theme.forecast.cardDividerBackground || theme.forecast.border};
      background: ${({ theme }) =>
        theme.forecast.cardGroupBackground || 'transparent'};
      min-height: auto;
      padding: 0.625rem 1rem;
    }

    .ant-card-head-title {
      font-weight: 600;
      font-size: 0.8125rem;
      color: ${({ theme }) => theme.forecast.activeText};
      padding: 0;
      letter-spacing: 0.01em;
    }

    .ant-card-body {
      padding: 1.25rem;
    }

    .ant-card-extra {
      padding: 0;
    }
  }
`

/**
 * TechniqueFormGrid - Grid layout for technique form fields
 */
const TechniqueFormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  gap: 1rem;

  .ant-form-item {
    margin-bottom: 0;
  }
`

/**
 * Inner section title used within technique cards and subsections
 */
const InnerSectionTitle = styled.h4`
  margin: 1.5rem 0 1rem 0;
  padding-top: 1.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.secondaryText};
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border-top: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};

  &:first-child {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }
`

/**
 * CalibrationTableWrapper - Container for calibration step table
 */
const CalibrationTableWrapper = styled.div`
  background: ${({ theme }) =>
    theme.forecast.cardGroupBackground || theme.forecast.background2};
  border-radius: 0.375rem;
  border: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
  overflow: hidden;
`

/**
 * Calibration table header row styling
 */
const CalibrationTableHeader = styled(Row)`
  && {
    padding: 0.625rem 0.75rem;
    background: ${({ theme }) =>
      theme.forecast.cardGroupBackground || theme.forecast.background2};
    border-bottom: 1px solid
      ${({ theme }) =>
        theme.forecast.cardDividerBackground || theme.forecast.border};

    .ant-typography {
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: ${({ theme }) => theme.forecast.secondaryText};
    }
  }
`

/**
 * Calibration table data row styling
 */
const CalibrationTableRow = styled(Row)`
  && {
    padding: 0.625rem 0.75rem;
    background: ${({ theme }) =>
      theme.forecast.cardBackground || theme.forecast.background1};
    border-bottom: 1px solid
      ${({ theme }) =>
        theme.forecast.cardDividerBackground || theme.forecast.border};
    align-items: center;

    &:last-child {
      border-bottom: none;
    }

    .ant-typography {
      font-size: 0.8125rem;
      color: ${({ theme }) => theme.forecast.activeText};
    }
  }
`

/**
 * Motor information column wrapper
 */
const MotorColumn = styled.div`
  padding: 1rem 1.25rem;
  background: ${({ theme }) =>
    theme.forecast.cardGroupBackground || theme.forecast.background2};
  border-radius: 0.5rem;
  border: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};

  .ant-form-item {
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

/**
 * Motor column title
 */
const MotorColumnTitle = styled(AntText)`
  && {
    display: block;
    font-weight: 600;
    font-size: 0.8125rem;
    color: ${({ theme }) => theme.forecast.activeText};
    margin-bottom: 1rem;
    padding-bottom: 0.625rem;
    letter-spacing: 0.01em;
    border-bottom: 1px solid
      ${({ theme }) =>
        theme.forecast.cardDividerBackground || theme.forecast.border};
  }
`

/**
 * MotorGridContainer - Grid layout for motor columns
 */
const MotorGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

/**
 * VerificationListContainer - Container for verification entries
 */
const VerificationListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

/**
 * Verification row wrapper for calibration verification entries
 */
const VerificationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: ${({ theme }) =>
    theme.forecast.cardGroupBackground || theme.forecast.background2};
  border-radius: 0.375rem;
  border: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};

  .ant-form-item {
    margin-bottom: 0;
    flex: 1;
  }

  .ant-picker {
    width: 100%;
  }
`

/**
 * VerificationLabel - Label for verification entries
 */
const VerificationLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.secondaryText};
  min-width: 6rem;
`

/**
 * Angle beam calibration card wrapper
 */
const AngleBeamCard = styled(Card)`
  && {
    margin-bottom: 0.75rem;
    border-radius: 0.375rem;
    border: 1px solid
      ${({ theme }) =>
        theme.forecast.cardDividerBackground || theme.forecast.border};
    background: ${({ theme }) =>
      theme.forecast.cardGroupBackground || theme.forecast.background2};

    &:last-of-type {
      margin-bottom: 0;
    }

    .ant-card-body {
      padding: 1rem 1.25rem;
    }

    .ant-card-extra {
      padding: 0;
    }
  }
`

/**
 * AngleBeamGrid - Grid layout for angle beam form fields
 */
const AngleBeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  .ant-form-item {
    margin-bottom: 0;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

/**
 * ActionButtonsRow - Container for action buttons
 */
const ActionButtonsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
`

export type SurfaceType =
  | 'O.D. Surface'
  | 'I.D. Surface'
  | 'Head'
  | 'Shell'
  | 'Channel'

export type SurfaceCondition =
  | 'Painted/Coated'
  | 'Sand Blasted'
  | 'Power Buffed'
  | 'Rusted'
  | 'Poor'
  | 'Fair'
  | 'Clean'

export type ProbeType =
  | 'Single'
  | 'Dual'
  | 'Through'
  | 'TOFD'
  | 'PAUT'
  | 'Dual Linear 0°'

export type WaveMode = 'S-Wave' | 'L-Wave' | 'L/S-Wave'

const waveModes: WaveMode[] = ['S-Wave', 'L-Wave', 'L/S-Wave']

export type TechniqueName =
  | 'Rapid AUT (0°)'
  | 'PAUT (90°)'
  | 'PAUT (270°)'
  | 'PAUT (0°)'
  | 'PAUT (180°)'

const techniqueOptions: TechniqueName[] = [
  'Rapid AUT (0°)',
  'PAUT (90°)',
  'PAUT (270°)',
  'PAUT (0°)',
  'PAUT (180°)',
]

interface AutocompleteOption {
  label: string
  value: string
}

const wedgeTypeOptions = [
  { label: 'Water Path', value: 'Water Path' },
  {
    label: 'GR-RAUT-0L-1.00SP',
    value: 'GR-RAUT-0L-1.00SP',
  },
  {
    label: 'GR-RAUT-0L-1.50SP',
    value: 'GR-RAUT-0L-1.50SP',
  },
]

const frequencyOptions = [
  { label: '2.25 MHz', value: '2.25' },
  { label: '5 MHz', value: '5' },
  { label: '7.5 MHz', value: '7.5' },
  { label: '10 MHz', value: '10' },
]

const calBlockTypeOptions = [
  { label: '1/2" Step', value: '1/2" Step' },
  { label: '1" Step', value: '1" Step' },
  { label: 'TRICAL Block', value: 'TRICAL Block' },
  { label: 'Navship', value: 'Navship' },
  {
    label: 'Piping Cal Block',
    value: 'Piping Cal Block',
  },
]

interface BrowseableAutoCompleteProps {
  value?: string
  onChange?: (value: string) => void
  options: readonly AutocompleteOption[]
  placeholder?: string
}

const BrowseableAutoComplete: React.FC<BrowseableAutoCompleteProps> = ({
  value,
  onChange,
  options,
  placeholder,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [isTyping, setIsTyping] = useState(false)

  const displayedOptions = useMemo(() => {
    const normalizedSearch = searchText.trim().toUpperCase()

    if (!isTyping || !normalizedSearch) {
      return [...options]
    }

    return options.filter(({ label, value: optionValue }) => {
      const normalizedValue = optionValue.toUpperCase()
      const normalizedLabel = label.toUpperCase()

      return (
        normalizedValue.includes(normalizedSearch) ||
        normalizedLabel.includes(normalizedSearch)
      )
    })
  }, [isTyping, options, searchText])

  const handleFocus = useCallback(() => {
    setIsTyping(false)
    setSearchText('')
    setIsOpen(true)
  }, [])

  const handleBlur = useCallback(() => {
    setIsOpen(false)
    setIsTyping(false)
    setSearchText('')
  }, [])

  const handleSearch = useCallback((nextSearch: string) => {
    setSearchText(nextSearch)
    setIsTyping(true)
    setIsOpen(true)
  }, [])

  const handleChange = useCallback(
    (nextValue: string) => {
      onChange?.(nextValue)
    },
    [onChange]
  )

  const handleSelect = useCallback(() => {
    setIsOpen(false)
    setIsTyping(false)
    setSearchText('')
  }, [])

  return (
    <AutoComplete
      value={value}
      options={displayedOptions}
      open={isOpen}
      filterOption={false}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onSearch={handleSearch}
      onChange={handleChange}
      onSelect={handleSelect}
      placeholder={placeholder}
      style={{ width: '100%' }}
    />
  )
}

const extractMaterialAndNominalThickness = (
  mainInfo: MainInfo | undefined,
  nominalsByInspection: ReadonlyMap<
    string,
    GeckoMeasurement | GeckoMeasurementRange
  >
): {
  material: string | undefined
  nominalThickness: GeckoMeasurement | GeckoMeasurementRange | undefined
} => {
  const material = mainInfo?.material
  const nominalValues = Array.from(nominalsByInspection.values())

  if (nominalValues.length === 0) {
    return { material, nominalThickness: undefined }
  }

  const allMeasurements: GeckoMeasurement[] = []
  for (const nominal of nominalValues) {
    if (nominal instanceof GeckoMeasurement) {
      allMeasurements.push(nominal)
    } else if (nominal instanceof GeckoMeasurementRange) {
      allMeasurements.push(nominal.min)
      allMeasurements.push(nominal.max)
    }
  }

  if (allMeasurements.length === 0) {
    return { material, nominalThickness: undefined }
  }

  const sortedMeasurements = allMeasurements.sort((a, b) => a.num - b.num)
  const min = sortedMeasurements[0]
  const max = sortedMeasurements[sortedMeasurements.length - 1]

  if (min.num === max.num) {
    return { material, nominalThickness: min }
  }

  const nominalThickness = GeckoMeasurementRange.CreateFromStartEnd(min, max)
  return { material, nominalThickness }
}

const createMeasurementFieldProps = (displayUnit: GeckoMeasurementUnit) => ({
  normalize: (value: number | null | undefined) =>
    value === undefined || value === null
      ? undefined
      : GeckoMeasurement.CreateFromValue(value, displayUnit),
  getValueProps: (
    value:
      | GeckoMeasurement
      | {
          num?: number
          unit?: string
        }
      | number
      | undefined
  ) => {
    if (value instanceof GeckoMeasurement) {
      return { value: value.toNumber(displayUnit) }
    }

    if (typeof value === 'number') {
      return { value }
    }

    if (value && typeof value === 'object' && typeof value.num === 'number') {
      return { value: value.num }
    }

    return { value: undefined }
  },
})

const RenderReact: React.FC = () => {
  const {
    formData,
    handleCalibrationFinish,
    saveCalibrationSilent,
    registerPageSaveHandler,
    inspections,
    nominalsByInspection,
    hasPhasedArrayInspection,
    metadataEntryId,
  } = useRautReportOps()

  const initialValues = useMemo(
    () => formData.calibrationData,
    [formData.calibrationData]
  )

  const [form] = Form.useForm<CalibrationPageData>()
  const calibrationEntryMode =
    Form.useWatch('calibrationEntryMode', form) ??
    initialValues?.calibrationEntryMode ??
    DEFAULT_CALIBRATION_ENTRY_MODE
  const isCalibrationFileRequired = calibrationEntryMode !== 'manual'
  const [showCalibrationModeToggle, setShowCalibrationModeToggle] = useState(
    calibrationEntryMode !== DEFAULT_CALIBRATION_ENTRY_MODE
  )

  useEffect(() => {
    if (calibrationEntryMode !== DEFAULT_CALIBRATION_ENTRY_MODE) {
      setShowCalibrationModeToggle(true)
    }
  }, [calibrationEntryMode])

  const { computedProcedure, computedProcedureTitle } = useMemo(() => {
    const regularInspections = inspections.filter(
      (i): i is GeckoInspection => i.type === RolodexEntryType.INSPECTION
    )

    if (regularInspections.length === 0) {
      return {
        computedProcedure: 'GR-UT-003 Rev.1',
        computedProcedureTitle: 'Rapid AUT Examination',
      }
    }

    const procedures: string[] = []
    const procedureTitles: string[] = []

    for (const inspection of regularInspections) {
      const inspectionType = inspection.data.inspection_type
      if (inspectionType) {
        const procedure = TYPE_TO_PROCEDURE[inspectionType]
        const procedureTitle = TYPE_TO_METHOD_TITLE[inspectionType]

        if (procedure && !procedures.includes(procedure)) {
          procedures.push(procedure)
        }
        if (procedureTitle && !procedureTitles.includes(procedureTitle)) {
          procedureTitles.push(procedureTitle)
        }
      }
    }

    return {
      computedProcedure:
        procedures.length > 0 ? procedures.join(' & ') : 'GR-UT-003 Rev.1',
      computedProcedureTitle:
        procedureTitles.length > 0
          ? procedureTitles.join(' & ')
          : 'Rapid AUT Examination',
    }
  }, [inspections])

  // material and nominalThickness are available for future use
  useMemo(
    () =>
      extractMaterialAndNominalThickness(
        formData.mainInfo,
        nominalsByInspection
      ),
    [formData.mainInfo, nominalsByInspection]
  )
  const { measurementSystem, getGeometricUnit, getDistanceUnit } =
    useUnitSystem()

  const geometricUnit = getGeometricUnit()
  const distanceUnit = getDistanceUnit()

  const getUnitDisplayString = (unit: GeckoMeasurementUnit): string => {
    switch (unit) {
      case GeckoMeasurementUnit.Inches:
        return 'in'
      case GeckoMeasurementUnit.Millimeters:
        return 'mm'
      case GeckoMeasurementUnit.Feet:
        return 'ft'
      case GeckoMeasurementUnit.Meters:
        return 'meter'
      default:
        return unit.id
    }
  }

  const geometricMeasurementFieldProps = useMemo(
    () => createMeasurementFieldProps(geometricUnit),
    [geometricUnit]
  )

  useEffect(() => {
    if (initialValues) {
      const techniques = initialValues.techniques || [
        {
          techniqueName: 'Rapid AUT (0°)' as TechniqueName,
          probeType: 'PAUT' as ProbeType,
          probePartNumber: '',
          probeSN: '',
          wedgeType: '',
          waveMode: 'L-Wave' as WaveMode,
          frequency: '',
          elementQuantity: 0,
          pitch: GeckoMeasurement.CreateFromValue(0, geometricUnit),
          angle: '0°',
          scanningGain: 0,
          velocity: GeckoMeasurement.CreateFromValue(0, geometricUnit),
        },
      ]

      const formattedValues = {
        ...initialValues,
        calibrationEntryMode:
          initialValues.calibrationEntryMode || DEFAULT_CALIBRATION_ENTRY_MODE,
        procedure: initialValues.procedure || computedProcedure,
        scanMethods: initialValues.scanMethods || computedProcedureTitle,
        techniques,
        xMotor: {
          ...initialValues.xMotor,
          function: initialValues.xMotor?.function || 'Index',
        },
        yMotor: {
          ...initialValues.yMotor,
          function: initialValues.yMotor?.function || 'Scanning',
        },
      }
      form.setFieldsValue(
        normalizeCalibrationFormData(formattedValues as CalibrationPageData, {
          geometricUnit,
          distanceUnit,
          hasPhasedArrayInspection,
        })
      )
    } else {
      const defaultValues: Partial<CalibrationPageData> = {
        calibrationEntryMode: DEFAULT_CALIBRATION_ENTRY_MODE,
        procedure: computedProcedure,
        scanMethods: computedProcedureTitle,
        techniques: [
          {
            techniqueName: 'Rapid AUT (0°)' as TechniqueName,
            probeType: 'PAUT' as ProbeType,
            probePartNumber: '',
            probeSN: '',
            wedgeType: '',
            waveMode: 'L-Wave' as WaveMode,
            frequency: '',
            elementQuantity: 0,
            pitch: GeckoMeasurement.CreateFromValue(0, geometricUnit),
            angle: '0°',
            scanningGain: 0,
            velocity: GeckoMeasurement.CreateFromValue(0, geometricUnit),
          },
        ],
        xMotor: {
          function: 'Index',
        },
        yMotor: {
          function: 'Scanning',
        },
      }
      form.setFieldsValue(
        normalizeCalibrationFormData(defaultValues as CalibrationPageData, {
          geometricUnit,
          distanceUnit,
          hasPhasedArrayInspection,
        })
      )
    }
  }, [
    form,
    initialValues,
    geometricUnit,
    distanceUnit,
    computedProcedure,
    computedProcedureTitle,
    hasPhasedArrayInspection,
  ])

  useEffect(() => {
    if (!form) return

    const currentValues = form.getFieldsValue()
    if (!currentValues) return

    form.setFieldsValue(
      normalizeCalibrationFormData(currentValues as CalibrationPageData, {
        geometricUnit,
        distanceUnit,
        hasPhasedArrayInspection,
      })
    )
  }, [
    distanceUnit,
    form,
    geometricUnit,
    hasPhasedArrayInspection,
    measurementSystem,
  ])

  const {
    isParsing: isCalibrationNdeParsing,
    isUploading: isCalibrationNdeUploading,
    parseError: calibrationNdeParseError,
    metadata: calibrationNdeMetadata,
    uploadCalibrationNde,
    deleteCalibrationNde,
  } = useCalibrationNdeUpload({
    metadataEntryId,
    calibrationData: formData.calibrationData,
    form,
    geometricUnit,
    distanceUnit,
    hasPhasedArrayInspection,
    saveCalibrationSilent,
  })

  const handleFinish = async (values: CalibrationPageData) => {
    try {
      const processedValues = normalizeCalibrationFormData(values, {
        geometricUnit,
        distanceUnit,
        hasPhasedArrayInspection,
      })
      const isComplete =
        calibrationPageDataSchema.safeParse(processedValues).success

      await handleCalibrationFinish(processedValues, isComplete)
    } catch (error) {
      console.error('Failed to save calibration page:', error)
      message.error('Failed to save calibration page')
      throw error
    }
  }

  const handleCalibrationImageUpload = useCallback(
    async (path: string, _imageKey: string, caption?: string) => {
      form.setFieldValue('calibrationImage', {
        imagePath: path,
        imageCaption: caption,
      })
      const currentValues = form.getFieldsValue(true) as CalibrationPageData
      const processedValues = normalizeCalibrationFormData(currentValues, {
        geometricUnit,
        distanceUnit,
        hasPhasedArrayInspection,
      })
      const isComplete =
        calibrationPageDataSchema.safeParse(processedValues).success
      await handleCalibrationFinish(processedValues, isComplete)
    },
    [
      distanceUnit,
      form,
      geometricUnit,
      hasPhasedArrayInspection,
      handleCalibrationFinish,
    ]
  )

  const geometricUnitStr = getUnitDisplayString(geometricUnit)

  const saveBeforeNavigation = useCallback(async () => {
    const values = form.getFieldsValue(true) as CalibrationPageData
    const processedValues = normalizeCalibrationFormData(values, {
      geometricUnit,
      distanceUnit,
      hasPhasedArrayInspection,
    })
    const isComplete =
      calibrationPageDataSchema.safeParse(processedValues).success

    await saveCalibrationSilent(processedValues, isComplete)
  }, [
    distanceUnit,
    form,
    geometricUnit,
    hasPhasedArrayInspection,
    saveCalibrationSilent,
  ])

  useEffect(() => {
    registerPageSaveHandler('calibration', saveBeforeNavigation)

    return () => {
      registerPageSaveHandler('calibration', null)
    }
  }, [registerPageSaveHandler, saveBeforeNavigation])

  return (
    <CalibrationPageWrapper>
      <StyledForm
        form={form}
        layout='vertical'
        onFinish={handleFinish}
        initialValues={initialValues}
        disabled={formData.lockState?.isLocked}>
        <FormSectionCard>
          <FormSectionHeader>Calibration Data File</FormSectionHeader>
          <Form.Item
            label='NDE Calibration File'
            required={isCalibrationFileRequired}
            colon={false}
            style={{ marginBottom: 0 }}>
            <CalibrationFileUpload
              disabled={formData.lockState?.isLocked}
              isParsing={isCalibrationNdeParsing}
              isUploading={isCalibrationNdeUploading}
              parseError={calibrationNdeParseError}
              metadata={calibrationNdeMetadata}
              onUpload={uploadCalibrationNde}
              onDelete={deleteCalibrationNde}
            />
          </Form.Item>
          <AdvancedOptionsRow>
            <Button
              type='link'
              size='small'
              onClick={() =>
                setShowCalibrationModeToggle((current) => !current)
              }>
              Advanced upload options
            </Button>
          </AdvancedOptionsRow>
          {showCalibrationModeToggle && (
            <AdvancedOptionsPanel>
              <Form.Item
                name='calibrationEntryMode'
                label='Entry Mode'
                initialValue={DEFAULT_CALIBRATION_ENTRY_MODE}
                style={{ marginBottom: 0 }}>
                <Radio.Group optionType='button' buttonStyle='solid'>
                  <Radio.Button value='auto'>Auto</Radio.Button>
                  <Radio.Button value='manual'>Manual</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </AdvancedOptionsPanel>
          )}
        </FormSectionCard>

        {/* Inspection Procedure Section */}
        <FormSectionCard>
          <FormSectionHeader>Inspection Procedure</FormSectionHeader>
          <FormFieldGroup>
            <Form.Item name='procedure' label='Procedure' required>
              <Input />
            </Form.Item>
            <Form.Item name='scanMethods' label='Procedure Title' required>
              <Input />
            </Form.Item>
          </FormFieldGroup>
        </FormSectionCard>

        {/* Inspection Equipment Section */}
        <FormSectionCard>
          <FormSectionHeader>Inspection Equipment</FormSectionHeader>
          <FormFieldGroup>
            <Form.Item name='testingUnit' label='Testing Unit' required>
              <AutoComplete
                options={[{ label: 'Omniscan', value: 'Omniscan' }]}
                filterOption={(inputValue, option) =>
                  option!.value
                    .toUpperCase()
                    .indexOf(inputValue.toUpperCase()) !== -1
                }
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item name='model' label='Model' required>
              <AutoComplete
                options={[
                  { label: 'X3 32:128', value: 'X3 32:128' },
                  { label: 'X3 64:128', value: 'X3 64:128' },
                  { label: ' X4 64:128', value: 'X4 64:128' },
                  { label: 'Focus PX 32:128', value: 'Focus PX 32:128' },
                ]}
                filterOption={(inputValue, option) =>
                  option!.value
                    .toUpperCase()
                    .indexOf(inputValue.toUpperCase()) !== -1
                }
                style={{ width: '100%' }}
              />
            </Form.Item>
          </FormFieldGroup>
          <FormFieldGroup>
            <Form.Item name='instrumentSN' label='Instrument S/N' required>
              <Input />
            </Form.Item>
            <Form.Item
              name='instrumentCalibrationDate'
              label='Instrument Calibration Date'>
              <DatePicker style={{ width: '100%' }} format='MM/DD/YYYY' />
            </Form.Item>
          </FormFieldGroup>
        </FormSectionCard>

        {/* Inspection Techniques Section */}
        <FormSectionCard>
          <FormSectionHeader>Inspection Techniques</FormSectionHeader>
          <Form.List name='techniques'>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <TechniqueCard
                    key={field.key}
                    size='small'
                    title={`Technique ${index + 1}`}
                    extra={
                      fields.length > 1 ? (
                        <Button
                          type='link'
                          danger
                          size='small'
                          onClick={() => remove(field.name)}>
                          Delete
                        </Button>
                      ) : null
                    }>
                    <TechniqueFormGrid>
                      <Form.Item
                        name={[field.name, 'techniqueName']}
                        label='Technique'
                        rules={[{ required: true }]}>
                        <Select>
                          {techniqueOptions.map((technique) => (
                            <Option key={technique} value={technique}>
                              {technique}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        name={[field.name, 'probeType']}
                        label='Probe Type'
                        required>
                        <Select>
                          <Option value='PAUT'>PAUT</Option>
                          <Option value='Single'>Single</Option>
                          <Option value='Dual'>Dual</Option>
                          <Option value='Through'>Through</Option>
                          <Option value='TOFD'>TOFD</Option>
                          <Option value='Dual Linear 0°'>Dual Linear 0°</Option>
                        </Select>
                      </Form.Item>

                      <Form.Item
                        name={[field.name, 'probePartNumber']}
                        label='Probe Part #'
                        required>
                        <Input />
                      </Form.Item>

                      <Form.Item
                        name={[field.name, 'probeSN']}
                        label='Probe S/N'
                        required>
                        <Input />
                      </Form.Item>

                      <Form.Item
                        name={[field.name, 'wedgeType']}
                        label='Wedge Type'
                        required>
                        <BrowseableAutoComplete
                          options={wedgeTypeOptions}
                          placeholder='Select or enter a custom wedge type'
                        />
                      </Form.Item>

                      <Form.Item
                        name={[field.name, 'waveMode']}
                        label='Wave Mode'
                        required>
                        <Select>
                          {waveModes.map((mode) => (
                            <Option key={mode} value={mode}>
                              {mode}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        name={[field.name, 'frequency']}
                        label='Frequency'
                        required>
                        <BrowseableAutoComplete
                          options={frequencyOptions}
                          placeholder='Select or enter a custom frequency'
                        />
                      </Form.Item>

                      <Form.Item
                        name={[field.name, 'elementQuantity']}
                        label='Element Quantity'
                        required>
                        <InputNumber style={{ width: '100%' }} />
                      </Form.Item>

                      <Form.Item
                        name={[field.name, 'pitch']}
                        label='Pitch'
                        normalize={geometricMeasurementFieldProps.normalize}
                        getValueProps={
                          geometricMeasurementFieldProps.getValueProps
                        }
                        required>
                        <InputNumber
                          addonAfter={geometricUnitStr}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>

                      <Form.Item
                        name={[field.name, 'angle']}
                        label='Angle'
                        required>
                        <Input />
                      </Form.Item>

                      <Form.Item
                        name={[field.name, 'scanningGain']}
                        label='Reference Gain'
                        help='Scanning Gain = Reference Gain ± 6 dB'
                        required>
                        <InputNumber
                          addonAfter='dB'
                          step={0.1}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>

                      <Form.Item
                        name={[field.name, 'velocity']}
                        label='Velocity'
                        normalize={geometricMeasurementFieldProps.normalize}
                        getValueProps={
                          geometricMeasurementFieldProps.getValueProps
                        }
                        required>
                        <InputNumber
                          addonAfter={`${geometricUnitStr}/µs`}
                          step={0.01}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </TechniqueFormGrid>

                    {/* Calibration Details for this technique */}
                    <InnerSectionTitle>Calibration Details</InnerSectionTitle>

                    <TechniqueFormGrid>
                      <Form.Item
                        name={[field.name, 'calibration', 'calBlockType']}
                        label='Cal Block Type'
                        required>
                        <BrowseableAutoComplete
                          options={calBlockTypeOptions}
                          placeholder='Select or enter a custom cal block type'
                        />
                      </Form.Item>

                      <Form.Item
                        name={[field.name, 'calibration', 'calBlockSN']}
                        label='Cal Block S/N'
                        required>
                        <Input />
                      </Form.Item>

                      <Form.Item
                        name={[field.name, 'calibration', 'reflectorType']}
                        label='Reflector Type'
                        required>
                        <Select>
                          <Option value='Steps'>Steps</Option>
                          <Option value='SDH'>SDH</Option>
                        </Select>
                      </Form.Item>
                    </TechniqueFormGrid>

                    {/* 0° Calibration - only for Rapid AUT */}
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const techniqueName = form.getFieldValue([
                          'techniques',
                          field.name,
                          'techniqueName',
                        ])
                        const isRapidAUT =
                          techniqueName && techniqueName.includes('Rapid AUT')

                        if (!isRapidAUT) return null

                        return (
                          <>
                            <InnerSectionTitle>
                              0° Calibration
                            </InnerSectionTitle>
                            <CalibrationTableWrapper>
                              <CalibrationTableHeader gutter={16}>
                                <Col span={8}>
                                  <AntText>Step</AntText>
                                </Col>
                                <Col span={8}>
                                  <AntText>
                                    Distance ({geometricUnitStr})
                                  </AntText>
                                </Col>
                                <Col span={8}>
                                  <AntText>
                                    Tolerance ({geometricUnitStr})
                                  </AntText>
                                </Col>
                              </CalibrationTableHeader>

                              <CalibrationTableRow gutter={16}>
                                <Col span={8}>
                                  <AntText>0° Thin Step</AntText>
                                </Col>
                                <Col span={8}>
                                  <Form.Item
                                    name={[
                                      field.name,
                                      'calibration',
                                      'thinStepDistance',
                                    ]}
                                    normalize={
                                      geometricMeasurementFieldProps.normalize
                                    }
                                    getValueProps={
                                      geometricMeasurementFieldProps.getValueProps
                                    }
                                    required
                                    style={{ marginBottom: 0 }}>
                                    <InputNumber style={{ width: '100%' }} />
                                  </Form.Item>
                                </Col>
                                <Col span={8}>
                                  <Form.Item style={{ marginBottom: 0 }}>
                                    <Input
                                      style={{ width: '100%' }}
                                      value={`± ${geometricUnit === GeckoMeasurementUnit.Inches ? '0.005' : '0.127'}`}
                                      disabled
                                    />
                                  </Form.Item>
                                </Col>
                              </CalibrationTableRow>

                              <CalibrationTableRow gutter={16}>
                                <Col span={8}>
                                  <AntText>0° Thick Step</AntText>
                                </Col>
                                <Col span={8}>
                                  <Form.Item
                                    name={[
                                      field.name,
                                      'calibration',
                                      'thickStepDistance',
                                    ]}
                                    normalize={
                                      geometricMeasurementFieldProps.normalize
                                    }
                                    getValueProps={
                                      geometricMeasurementFieldProps.getValueProps
                                    }
                                    required
                                    style={{ marginBottom: 0 }}>
                                    <InputNumber style={{ width: '100%' }} />
                                  </Form.Item>
                                </Col>
                                <Col span={8}>
                                  <Form.Item style={{ marginBottom: 0 }}>
                                    <Input
                                      style={{ width: '100%' }}
                                      value={`± ${geometricUnit === GeckoMeasurementUnit.Inches ? '0.005' : '0.127'}`}
                                      disabled
                                    />
                                  </Form.Item>
                                </Col>
                              </CalibrationTableRow>

                              <CalibrationTableRow gutter={16}>
                                <Col span={8}>
                                  <AntText>Verification Step</AntText>
                                </Col>
                                <Col span={8}>
                                  <Form.Item
                                    name={[
                                      field.name,
                                      'calibration',
                                      'verificationStepDistance',
                                    ]}
                                    normalize={
                                      geometricMeasurementFieldProps.normalize
                                    }
                                    getValueProps={
                                      geometricMeasurementFieldProps.getValueProps
                                    }
                                    required
                                    style={{ marginBottom: 0 }}>
                                    <InputNumber style={{ width: '100%' }} />
                                  </Form.Item>
                                </Col>
                                <Col span={8}>
                                  <Form.Item style={{ marginBottom: 0 }}>
                                    <Input
                                      style={{ width: '100%' }}
                                      value={`± ${geometricUnit === GeckoMeasurementUnit.Inches ? '0.005' : '0.127'}`}
                                      disabled
                                    />
                                  </Form.Item>
                                </Col>
                              </CalibrationTableRow>
                            </CalibrationTableWrapper>
                          </>
                        )
                      }}
                    </Form.Item>
                  </TechniqueCard>
                ))}

                <Form.Item>
                  <Button
                    type='dashed'
                    onClick={() =>
                      add({
                        id: crypto.randomUUID(),
                        techniqueName: 'Rapid AUT (0°)',
                        probeType: 'PAUT',
                        probePartNumber: '',
                        probeSN: '',
                        wedgeType: '',
                        waveMode: 'L-Wave',
                        frequency: '',
                        elementQuantity: 0,
                        pitch: GeckoMeasurement.CreateFromValue(
                          0,
                          geometricUnit
                        ),
                        angle: '0°',
                        scanningGain: 0,
                        velocity: GeckoMeasurement.CreateFromValue(
                          0,
                          geometricUnit
                        ),
                      })
                    }
                    block
                    icon={<PlusOutlined />}>
                    Add Technique
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          {/* Global Angle Beam Calibration - shows if ANY technique is PAUT */}
          <Form.Item noStyle shouldUpdate>
            {() => {
              const techniques = form.getFieldValue('techniques') || []
              const hasPAUT = techniques.some(
                (t: { techniqueName?: string }) =>
                  t?.techniqueName && t.techniqueName.includes('PAUT')
              )

              if (!hasPAUT) return null

              return (
                <div style={{ marginTop: '1.5rem' }}>
                  <InnerSectionTitle style={{ marginTop: 0 }}>
                    Angle Beam Calibration
                  </InnerSectionTitle>
                  <Form.List name='angleBeamCalibration'>
                    {(angleFields, { add, remove }) => (
                      <>
                        {angleFields.map((angleField) => (
                          <AngleBeamCard
                            key={angleField.key}
                            size='small'
                            extra={
                              <Button
                                type='link'
                                danger
                                size='small'
                                onClick={() => remove(angleField.name)}>
                                Delete
                              </Button>
                            }>
                            <AngleBeamGrid>
                              <Form.Item
                                name={[angleField.name, 'name']}
                                label='Name'
                                required>
                                <Input />
                              </Form.Item>
                              <Form.Item
                                name={[angleField.name, 'distance']}
                                label='Distance'
                                normalize={
                                  geometricMeasurementFieldProps.normalize
                                }
                                getValueProps={
                                  geometricMeasurementFieldProps.getValueProps
                                }
                                required>
                                <InputNumber
                                  addonAfter={geometricUnitStr}
                                  style={{ width: '100%' }}
                                />
                              </Form.Item>
                              <Form.Item
                                name={[angleField.name, 'amplitude']}
                                label='Amplitude'
                                required>
                                <Input addonAfter='%' />
                              </Form.Item>
                            </AngleBeamGrid>
                          </AngleBeamCard>
                        ))}
                        <Form.Item
                          style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                          <Button
                            type='dashed'
                            onClick={() =>
                              add({
                                id: crypto.randomUUID(),
                                name: '',
                                distance: GeckoMeasurement.CreateFromValue(
                                  0,
                                  geometricUnit
                                ),
                                amplitude: '',
                              })
                            }
                            block>
                            Add Angle Beam Entry
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </div>
              )
            }}
          </Form.Item>

          <InnerSectionTitle>Scanner Information</InnerSectionTitle>
          <FormFieldGroup>
            <Form.Item name={'scannerType'} label='Scanner Type' required>
              <AutoComplete
                options={[
                  { label: 'Navic', value: 'Navic' },
                  { label: 'Skoot', value: 'Skoot' },
                ]}
                filterOption={(inputValue, option) =>
                  option!.value
                    .toUpperCase()
                    .indexOf(inputValue.toUpperCase()) !== -1
                }
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item name='scannerSN' label='Scanner S/N' required>
              <Input />
            </Form.Item>
          </FormFieldGroup>
        </FormSectionCard>

        {/* Motor Information Section */}
        <FormSectionCard>
          <FormSectionHeader>Motor Information</FormSectionHeader>
          <MotorGridContainer>
            <MotorColumn>
              <MotorColumnTitle>X-Motor</MotorColumnTitle>
              <Form.Item
                name={['xMotor', 'function']}
                label='Function'
                rules={[
                  {
                    required: true,
                    message: 'Please select X-Motor function',
                  },
                ]}>
                <Select>
                  <Option value='Index'>Index</Option>
                  <Option value='Scanning'>Scanning</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name={['xMotor', 'scanRes']}
                label='Scan Res.'
                normalize={geometricMeasurementFieldProps.normalize}
                getValueProps={geometricMeasurementFieldProps.getValueProps}>
                <InputNumber
                  addonAfter={geometricUnitStr}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item
                name={['xMotor', 'speed']}
                label='Speed'
                normalize={geometricMeasurementFieldProps.normalize}
                getValueProps={geometricMeasurementFieldProps.getValueProps}>
                <InputNumber
                  addonAfter={`${geometricUnitStr}/sec`}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </MotorColumn>
            <MotorColumn>
              <MotorColumnTitle>Y-Motor</MotorColumnTitle>
              <Form.Item
                name={['yMotor', 'function']}
                label='Function'
                rules={[
                  {
                    required: true,
                    message: 'Please select Y-Motor function',
                  },
                ]}>
                <Select>
                  <Option value='Index'>Index</Option>
                  <Option value='Scanning'>Scanning</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name={['yMotor', 'scanRes']}
                label='Scan Res.'
                normalize={geometricMeasurementFieldProps.normalize}
                getValueProps={geometricMeasurementFieldProps.getValueProps}>
                <InputNumber
                  addonAfter={geometricUnitStr}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item
                name={['yMotor', 'speed']}
                label='Speed'
                normalize={geometricMeasurementFieldProps.normalize}
                getValueProps={geometricMeasurementFieldProps.getValueProps}>
                <InputNumber
                  addonAfter={`${geometricUnitStr}/sec`}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </MotorColumn>
          </MotorGridContainer>
        </FormSectionCard>

        {/* Calibration Verification Section */}
        <FormSectionCard>
          <FormSectionHeader>Calibration Verification</FormSectionHeader>
          <Form.List name='calibrationVerification'>
            {(fields, { add, remove }) => {
              const handleSortChronologically = () => {
                const currentValues =
                  form.getFieldValue('calibrationVerification') || []
                const sorted = [...currentValues].sort(
                  (a: { date: unknown }, b: { date: unknown }) => {
                    if (!a.date || !b.date) return 0
                    const getTimestamp = (date: unknown): number => {
                      if (!date) return 0
                      if (
                        typeof date === 'object' &&
                        date !== null &&
                        'valueOf' in date
                      ) {
                        return (date as { valueOf: () => number }).valueOf()
                      }
                      if (typeof date === 'string') {
                        return new Date(date).getTime()
                      }
                      if (date instanceof Date) {
                        return date.getTime()
                      }
                      return 0
                    }
                    return getTimestamp(a.date) - getTimestamp(b.date)
                  }
                )
                form.setFieldValue('calibrationVerification', sorted)
              }

              return (
                <>
                  <VerificationListContainer>
                    {fields.map((field, index) => (
                      <VerificationRow key={field.key}>
                        <VerificationLabel>
                          Verification {index + 1}
                        </VerificationLabel>
                        <Form.Item
                          name={[field.name, 'date']}
                          required={false}
                          style={{ marginBottom: 0 }}>
                          <DatePicker
                            showTime
                            style={{ width: '100%' }}
                            format='YYYY-MM-DD HH:mm:ss'
                          />
                        </Form.Item>
                        <Button
                          onClick={() => remove(field.name)}
                          type='link'
                          danger
                          size='small'>
                          Delete
                        </Button>
                      </VerificationRow>
                    ))}
                  </VerificationListContainer>
                  <ActionButtonsRow>
                    <Button type='dashed' onClick={() => add({ date: null })}>
                      Add Calibration Verification
                    </Button>
                    {fields.length > 1 && (
                      <Button onClick={handleSortChronologically}>
                        Sort Chronologically
                      </Button>
                    )}
                  </ActionButtonsRow>
                </>
              )
            }}
          </Form.List>
        </FormSectionCard>

        <FormSectionCard>
          <FormSectionHeader>
            <RequiredAsterisk>*</RequiredAsterisk>
            Calibration Image
          </FormSectionHeader>
          <ImageUpload
            metadataEntryId={metadataEntryId}
            imageData={formData.calibrationData?.calibrationImage}
            onUpload={handleCalibrationImageUpload}
            fileNamePrefix='calibration-image'
            subpath='calibration'
            buttonText={
              formData.calibrationData?.calibrationImage?.imagePath
                ? 'Update Calibration Image'
                : 'Upload Calibration Image'
            }
            maxSizeInMB={10}
            imageKey='calibrationImage'
          />
        </FormSectionCard>
      </StyledForm>
    </CalibrationPageWrapper>
  )
}

type RenderableValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Dayjs
  | GeckoMeasurement
  | GeckoMeasurementRange

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData, nominalsByInspection, calibrationImage } =
    useRautReportOps()
  const { isMetric } = useUnitSystem()

  // Only use the calibration image if it's a valid base64 data URL
  // (signed HTTP URLs will fail in @react-pdf/renderer's pdf().toBlob())
  const calibrationImageUrl = useMemo(() => {
    const url = calibrationImage?.signedUrl
    if (!url) return undefined
    if (!url.startsWith('data:')) {
      console.warn(
        '[Calibration RenderPDF] calibrationImage.signedUrl is not a data URL, skipping image page'
      )
      return undefined
    }
    return url
  }, [calibrationImage?.signedUrl])

  const data = useMemo(
    () => formData.calibrationData,
    [formData.calibrationData]
  )

  const { material, nominalThickness: nominalThicknessRange } = useMemo(
    () =>
      extractMaterialAndNominalThickness(
        formData.mainInfo,
        nominalsByInspection
      ),
    [formData.mainInfo, nominalsByInspection]
  )

  // Sort calibration verification dates chronologically
  const sortedCalibrationVerification = useMemo(() => {
    if (
      !data.calibrationVerification ||
      data.calibrationVerification.length === 0
    ) {
      return []
    }

    return [...data.calibrationVerification].sort((a, b) => {
      if (!a.date || !b.date) return 0

      const getTimestamp = (date: unknown): number => {
        if (typeof date === 'object' && date !== null && 'valueOf' in date) {
          return (date as { valueOf: () => number }).valueOf()
        }
        if (typeof date === 'string') {
          return new Date(date).getTime()
        }
        if (date instanceof Date) {
          return date.getTime()
        }
        return 0
      }

      return getTimestamp(a.date) - getTimestamp(b.date)
    })
  }, [data.calibrationVerification])

  const renderRow = (
    label: string,
    value: RenderableValue,
    compact = false
  ) => {
    let displayValue: string = 'Not specified'

    if (value !== undefined && value !== null) {
      if (typeof value === 'boolean') {
        displayValue = value ? 'Yes' : 'No'
      } else if (
        typeof value === 'object' &&
        'format' in value &&
        typeof value.format === 'function'
      ) {
        displayValue = value.format('MM/DD/YYYY')
      } else if (value instanceof GeckoMeasurementRange) {
        displayValue = `${value.min.num} - ${value.max.num} ${value.min.unit.id}`
      } else if (value instanceof GeckoMeasurement) {
        displayValue = `${value.num} ${value.unit.id}`
      } else {
        displayValue = String(value)
      }
    }

    return (
      <View style={compact ? PDFStyles.compactRow : PDFStyles.row}>
        <Text style={PDFStyles.label}>{label}:</Text>
        <Text style={PDFStyles.value}>{displayValue}</Text>
      </View>
    )
  }

  // Safely extract unit ID from a GeckoMeasurement that may be a class instance
  // or a plain JSON object (e.g. { num: 5, unit: "in" }) after round-tripping through the server
  const getUnitId = (unit: unknown): string => {
    if (!unit) return ''
    if (typeof unit === 'string') return unit
    if (typeof unit === 'object' && unit !== null && 'id' in unit)
      return String((unit as { id: string }).id)
    return ''
  }

  const formatGeckoMeasurement = (
    measurement?: GeckoMeasurement,
    options?: { suffix?: string; fractionDigits?: number }
  ): string => {
    if (!measurement) return 'Not specified'
    const unitId = getUnitId(measurement.unit)
    const numStr = measurement.num.toFixed(options?.fractionDigits ?? 3)
    return `${numStr}${unitId ? ` ${unitId}` : ''}${options?.suffix ? ' ' + options.suffix : ''}`
  }

  return (
    <>
      <Page size='A4' style={PDFStyles.page} id='calibration'>
        {/* Hidden text for page tracking */}
        <Text
          render={({ pageNumber }) => {
            onRender({ id: 'calibration', pageNumber })
            return ''
          }}
        />
        <PageHeader title='Calibration Information' />
        <View style={PDFStyles.contentWithPadding}>
          {/* Asset Information */}
          <View style={PDFStyles.section}>
            <Text style={PDFStyles.sectionHeader}>Asset Information</Text>
            <View style={PDFStyles.columns}>
              <View style={PDFStyles.columnLeft}>
                {renderRow('Asset', formData.mainInfo?.asset)}
                {renderRow('Material', material)}
                {renderRow('Nominal', nominalThicknessRange)}
                {renderRow(
                  'Diameter',
                  formatGeckoMeasurement(formData.mainInfo?.diameter, {
                    fractionDigits: isMetric ? 3 : undefined,
                  })
                )}
                {renderRow(
                  'Length',
                  formatGeckoMeasurement(formData.mainInfo?.length, {
                    fractionDigits: isMetric ? 3 : undefined,
                  })
                )}
              </View>
              <View style={PDFStyles.columnRight}>
                {renderRow(
                  'Inspection Surface',
                  formData.mainInfo?.inspectionSurface
                )}
                {renderRow(
                  'Surface Condition',
                  formData.mainInfo?.surfaceCondition
                )}
                {renderRow('Coating', formData.mainInfo?.coated ? 'Yes' : 'No')}
                {renderRow(
                  'Temperature',
                  formData.mainInfo?.temperatureUnit === 'Ambient'
                    ? 'Ambient'
                    : `${formData.mainInfo?.surfaceTemperature} ${formData.mainInfo?.temperatureUnit}`
                )}
              </View>
            </View>
          </View>

          {/* Inspection Procedure */}
          <View style={PDFStyles.section}>
            <Text style={PDFStyles.sectionHeader}>Inspection Procedure</Text>
            {renderRow('Procedure', data.procedure)}
            {renderRow('Procedure Title', data.scanMethods)}
          </View>

          {/* Inspection Equipment */}
          <View style={PDFStyles.section}>
            <Text style={PDFStyles.sectionHeader}>Inspection Equipment</Text>

            {/* Testing Unit & Scanner/Motors in two columns */}
            <View style={PDFStyles.columns}>
              <View style={PDFStyles.columnLeft}>
                {renderRow('Testing Unit', data.testingUnit)}
                {renderRow('Model', data.model)}
                {renderRow('Unit Serial #', data.instrumentSN)}
                {data.instrumentCalibrationDate &&
                  renderRow(
                    'Calibration Date',
                    formatDate(data.instrumentCalibrationDate)
                  )}

                {/* Technique details - aligned with Testing Unit values */}
                {data.techniques?.map((technique, techIndex) => (
                  <View key={techIndex}>
                    {renderRow('Probe Type', technique.probeType)}
                    {renderRow('Probe Part #', technique.probePartNumber)}
                    {renderRow('Probe S/N', technique.probeSN)}
                    {renderRow('Wedge', technique.wedgeType)}
                    {renderRow('Frequency', `${technique.frequency} MHz`)}
                    {renderRow('Element Qty', technique.elementQuantity)}
                    {renderRow(
                      'Pitch',
                      formatGeckoMeasurement(technique.pitch)
                    )}
                    {renderRow('Angle', technique.angle)}
                    {renderRow(
                      'Ref. (+Scan) Gain',
                      `${technique.scanningGain} dB`
                    )}
                    {renderRow(
                      'Velocity',
                      technique.velocity
                        ? `${technique.velocity.num} ${getUnitId(technique.velocity.unit)}/µs`
                        : 'Not specified'
                    )}
                  </View>
                ))}
              </View>
              <View style={PDFStyles.columnRight}>
                {renderRow('Scanner Type', data.scannerType)}
                {renderRow('Scanner Serial #', data.scannerSN)}

                {/* Motors Table */}
                <View style={{ marginTop: 8 }}>
                  <Text
                    style={[PDFStyles.subsectionTitle, { marginBottom: 4 }]}>
                    Motors
                  </Text>
                  <View style={{ flexDirection: 'row' }}>
                    <Text style={[PDFStyles.label, { flex: 1 }]}></Text>
                    <Text
                      style={[
                        PDFStyles.subsectionTitle,
                        { flex: 1, textAlign: 'center' },
                      ]}>
                      X-Axis
                    </Text>
                    <Text
                      style={[
                        PDFStyles.subsectionTitle,
                        { flex: 1, textAlign: 'center' },
                      ]}>
                      Y-Axis
                    </Text>
                  </View>

                  {/* Function Row */}
                  <View style={PDFStyles.row}>
                    <Text style={[PDFStyles.label, { flex: 1 }]}>Function</Text>
                    <Text
                      style={[
                        PDFStyles.value,
                        { flex: 1, textAlign: 'center' },
                      ]}>
                      {data.xMotor?.function || 'Index'}
                    </Text>
                    <Text
                      style={[
                        PDFStyles.value,
                        { flex: 1, textAlign: 'center' },
                      ]}>
                      {data.yMotor?.function || 'Scanning'}
                    </Text>
                  </View>

                  {/* Resolution Row */}
                  <View style={PDFStyles.row}>
                    <Text style={[PDFStyles.label, { flex: 1 }]}>
                      Resolution
                    </Text>
                    <Text
                      style={[
                        PDFStyles.value,
                        { flex: 1, textAlign: 'center' },
                      ]}>
                      {formatGeckoMeasurement(data.xMotor?.scanRes)}
                    </Text>
                    <Text
                      style={[
                        PDFStyles.value,
                        { flex: 1, textAlign: 'center' },
                      ]}>
                      {formatGeckoMeasurement(data.yMotor?.scanRes)}
                    </Text>
                  </View>

                  {/* Speed Row */}
                  <View style={PDFStyles.row}>
                    <Text style={[PDFStyles.label, { flex: 1 }]}>Speed</Text>
                    <Text
                      style={[
                        PDFStyles.value,
                        { flex: 1, textAlign: 'center' },
                      ]}>
                      {data.xMotor?.speed
                        ? `${data.xMotor.speed.num} ${getUnitId(data.xMotor.speed.unit)}/sec`
                        : 'Not specified'}
                    </Text>
                    <Text
                      style={[
                        PDFStyles.value,
                        { flex: 1, textAlign: 'center' },
                      ]}>
                      {data.yMotor?.speed
                        ? `${data.yMotor.speed.num} ${getUnitId(data.yMotor.speed.unit)}/sec`
                        : 'Not specified'}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* Calibration Details */}
          <View style={PDFStyles.section}>
            <Text style={PDFStyles.sectionHeader}>Calibration Details</Text>

            {/* Two-column layout for calibration details */}
            <View style={PDFStyles.columns}>
              {/* Left Column - Cal Block Info & Verification Dates */}
              <View style={PDFStyles.columnLeft}>
                {/* Cal Block Type */}
                {renderRow(
                  'Cal Block Type',
                  data.techniques?.[0]?.calibration?.calBlockType,
                  true
                )}
                {/* Cal Block Material */}
                {renderRow('Cal Block Material', material, true)}
                {/* Cal Block S/N */}
                {renderRow(
                  'Cal Block S/N',
                  data.techniques?.[0]?.calibration?.calBlockSN,
                  true
                )}
                {/* Reflector Type */}
                {renderRow(
                  'Reflector Type',
                  data.techniques?.[0]?.calibration?.reflectorType,
                  true
                )}

                {/* Calibration Verification Dates */}
                {sortedCalibrationVerification.length > 0 && (
                  <View style={{ marginTop: 6 }}>
                    <Text style={PDFStyles.subsectionTitle}>
                      Calibration Verification Dates
                    </Text>
                    {sortedCalibrationVerification.map(
                      (verification, index) => (
                        <View key={index} style={PDFStyles.compactRow}>
                          <Text style={PDFStyles.label}>
                            Verification {index + 1}:
                          </Text>
                          <Text style={PDFStyles.value}>
                            {verification.date
                              ? formatDate(verification.date, true)
                              : 'No date specified'}
                          </Text>
                        </View>
                      )
                    )}
                  </View>
                )}
              </View>

              {/* Right Column - 0° Calibration Table */}
              <View style={PDFStyles.columnRight}>
                {(() => {
                  const rapidAUTTechnique = data.techniques?.find((t) =>
                    t.techniqueName?.includes('Rapid AUT')
                  )
                  if (rapidAUTTechnique?.calibration) {
                    return (
                      <>
                        <Text style={PDFStyles.subsectionTitle}>
                          0° Calibration
                        </Text>
                        <View
                          style={{
                            flexDirection: 'row',
                            borderBottomWidth: 1,
                            borderColor: '#ccc',
                            paddingBottom: 2,
                            marginTop: 4,
                            marginBottom: 4,
                          }}>
                          <Text
                            style={{
                              flex: 2,
                              fontSize: 8,
                              fontWeight: 'bold',
                            }}>
                            Step
                          </Text>
                          <Text
                            style={{
                              flex: 1,
                              fontSize: 8,
                              fontWeight: 'bold',
                              textAlign: 'right',
                            }}>
                            Distance
                          </Text>
                          <Text
                            style={{
                              flex: 1,
                              fontSize: 8,
                              fontWeight: 'bold',
                              textAlign: 'right',
                            }}>
                            Tolerance
                          </Text>
                        </View>
                        {/* 0° Thin Step */}
                        <View
                          style={{
                            flexDirection: 'row',
                            paddingVertical: 2,
                          }}>
                          <Text style={{ flex: 2, fontSize: 7 }}>
                            0° Thin Step:
                          </Text>
                          <Text
                            style={{
                              flex: 1,
                              fontSize: 7,
                              textAlign: 'right',
                            }}>
                            {formatGeckoMeasurement(
                              rapidAUTTechnique.calibration.thinStepDistance
                            )}
                          </Text>
                          <Text
                            style={{
                              flex: 1,
                              fontSize: 7,
                              textAlign: 'right',
                            }}>
                            {rapidAUTTechnique.calibration.thinStepTolerance
                              ? `± ${rapidAUTTechnique.calibration.thinStepTolerance.num} ${getUnitId(rapidAUTTechnique.calibration.thinStepTolerance.unit)}`
                              : '-'}
                          </Text>
                        </View>
                        {/* 0° Thick Step */}
                        <View
                          style={{
                            flexDirection: 'row',
                            paddingVertical: 2,
                          }}>
                          <Text style={{ flex: 2, fontSize: 7 }}>
                            0° Thick Step:
                          </Text>
                          <Text
                            style={{
                              flex: 1,
                              fontSize: 7,
                              textAlign: 'right',
                            }}>
                            {formatGeckoMeasurement(
                              rapidAUTTechnique.calibration.thickStepDistance
                            )}
                          </Text>
                          <Text
                            style={{
                              flex: 1,
                              fontSize: 7,
                              textAlign: 'right',
                            }}>
                            {rapidAUTTechnique.calibration.thickStepTolerance
                              ? `± ${rapidAUTTechnique.calibration.thickStepTolerance.num} ${getUnitId(rapidAUTTechnique.calibration.thickStepTolerance.unit)}`
                              : '-'}
                          </Text>
                        </View>
                        {/* Verification Step */}
                        <View
                          style={{
                            flexDirection: 'row',
                            paddingVertical: 2,
                          }}>
                          <Text style={{ flex: 2, fontSize: 7 }}>
                            Verification Step:
                          </Text>
                          <Text
                            style={{
                              flex: 1,
                              fontSize: 7,
                              textAlign: 'right',
                            }}>
                            {formatGeckoMeasurement(
                              rapidAUTTechnique.calibration
                                .verificationStepDistance
                            )}
                          </Text>
                          <Text
                            style={{
                              flex: 1,
                              fontSize: 7,
                              textAlign: 'right',
                            }}>
                            {rapidAUTTechnique.calibration
                              .verificationStepTolerance
                              ? `± ${rapidAUTTechnique.calibration.verificationStepTolerance.num} ${getUnitId(rapidAUTTechnique.calibration.verificationStepTolerance.unit)}`
                              : '-'}
                          </Text>
                        </View>
                      </>
                    )
                  }
                  return null
                })()}
              </View>
            </View>

            {/* Angle Beam Calibration - Full Width Below */}
            {data.angleBeamCalibration &&
              data.angleBeamCalibration.length > 0 && (
                <View style={{ marginTop: 10 }}>
                  <Text style={PDFStyles.subsectionTitle}>
                    Angle Beam Calibration
                  </Text>
                  <View
                    style={{
                      flexDirection: 'row',
                      borderBottomWidth: 1,
                      borderColor: '#ccc',
                      paddingBottom: 2,
                      marginTop: 4,
                      marginBottom: 4,
                    }}>
                    <Text style={{ flex: 2, fontSize: 8, fontWeight: 'bold' }}>
                      Name
                    </Text>
                    <Text
                      style={{
                        flex: 1,
                        fontSize: 8,
                        fontWeight: 'bold',
                        textAlign: 'right',
                      }}>
                      Distance
                    </Text>
                    <Text
                      style={{
                        flex: 1,
                        fontSize: 8,
                        fontWeight: 'bold',
                        textAlign: 'right',
                      }}>
                      Amplitude
                    </Text>
                  </View>
                  {data.angleBeamCalibration.map((entry, idx) => (
                    <View
                      key={idx}
                      style={{ flexDirection: 'row', paddingVertical: 2 }}>
                      <Text style={{ flex: 2, fontSize: 7 }}>{entry.name}</Text>
                      <Text
                        style={{ flex: 1, fontSize: 7, textAlign: 'right' }}>
                        {formatGeckoMeasurement(entry.distance)}
                      </Text>
                      <Text
                        style={{ flex: 1, fontSize: 7, textAlign: 'right' }}>
                        {entry.amplitude}%
                      </Text>
                    </View>
                  ))}
                </View>
              )}
          </View>
        </View>
        {/* Page footer */}
        <PageFooter
          jobNumber={formData.coverPageData.geckoJobId}
          revisionNumber={formData.revisionMetadata.revisionNumber}
          prelim={
            !(
              formData.signoff?.reviewerSignoff &&
              formData.signoff.supervisorSignoff
            )
          }
        />{' '}
      </Page>

      {/* Second page for Calibration Image (landscape orientation) - only shown if user uploaded an image */}
      {calibrationImageUrl && (
        <Page
          size='A4'
          orientation='landscape'
          style={PDFStyles.page}
          id='calibrationImages'>
          {/* Hidden text for page tracking */}
          <Text
            render={({ pageNumber }) => {
              onRender({ id: 'calibrationImages', pageNumber })
              return ''
            }}
          />

          <PageHeader title='Calibration Images' />

          <View style={PDFStyles.content}>
            <View style={{ alignItems: 'flex-start' }}>
              <PageSubheader
                title='Calibration Image'
                align='left'
                color='black'
              />
            </View>

            <Text style={PDFStyles.imageDescription}>
              A dynamic calibration scan is performed daily. The below image
              represents this dynamic scan for the asset.
            </Text>

            <View style={PDFStyles.imageContainer}>
              <Image src={calibrationImageUrl} style={PDFStyles.image} />
            </View>
          </View>
          {/* Page footer */}
          <PageFooter
            jobNumber={formData.coverPageData.geckoJobId}
            revisionNumber={formData.revisionMetadata.revisionNumber}
          />
        </Page>
      )}
    </>
  )
}

export const Calibration: ReportSection = {
  RenderReact,
  RenderPDF,
  required: true,
  description: 'Equipment calibration details',
  id: 'calibration',
  title: 'Calibration Information',
  requiredFields: requiredCalibrationFields,
}
